"use client";
import React, { useCallback, useMemo, useState } from "react";
import UnivoCarousel, {
  useUnivoCarousel,
} from "@/components/common/UnivoCarousel";
import type { UnivoCarouselSettings } from "@/components/common/UnivoCarousel/types";
import {
  STUDENT_STORIES_DATA,
  REVIEWS_VIDEO_DATA_REVAMP,
  STUDENT_STORIES_DATA_BRAND
} from "@/data/homepage-revamp-static-data";
import styles from "./Reviews.module.scss";
import clsx from "clsx";
import LeftArrowNoTail from "@/SvgIcons/LeftArrowNoTail";
import RightArrowNoTail from "@/SvgIcons/RightArrowNoTail";
import { IModalActions, ModalTypes } from "@/types/modal";
import { useModalDispatch } from "@/context/ModalContext";
import VideoSlide from "./VideoSlide";
import { mergeArrayAlternatively } from "@/helpers/mergeArrayAlternatively";
import cn from 'clsx';
import { addStaticSubdomain } from "@/revamp/molecules/ProgramCard/utils";
import { getYouTubeEmbedUrl } from "@/data/homepage-revamp-static-data";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import { GA_EVENTS, GAEvent } from "@/helpers/gaEvents";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";
import UnivoImage from "@/components/common/UnivoImage";
import { IMAGE_ASSETS } from "@/constants/images";


const Reviews2 = ({associatedPrograms}) => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const { ref: sliderRef, next: handleNext, prev: handlePrev } =
    useUnivoCarousel();
  const modalDispatch = useModalDispatch();
  const { LEAD_INITIATE } = GAEvent;

  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);

  const handleOpenModal = (link: string) => {
    setCurrentVideoUrl(link);
  };

  const handleCloseModal = () => {
    setCurrentVideoUrl(null);
  };

  const finalReviews = mergeArrayAlternatively(
    STUDENT_STORIES_DATA,
  );

  const handleSlideChange = useCallback((swiper: { isBeginning: boolean; isEnd: boolean }) => {
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  }, []);

  const carouselSettings = useMemo<UnivoCarouselSettings>(
    () => ({
      pagination: {
        status: false,
        className: `${styles.bulletsContainer} revamp_carousel_pagination`,
      },
      onSlideChange: handleSlideChange,
    }),
    [handleSlideChange]
  );

  const handleTestimonial = ({ text, sent_by, rating }) => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.TestimonialModal,
        modalData: { text, sent_by, rating },
      },
    });
  };

  const handlePlayClick = (videoUrl: string) => {
    handleOpenModal(videoUrl);
  };

  const handleBtnClicked = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.BrandModal,
        modalData: {
          heading: "",
          submitBtnText: "Book a Free Session",
          isLandingPage: true,
          associatedPrograms,
          brandImage:{
            dWeb: "https://static.amityonline.com/_s/Displaying_image_1_cb445b47a1.webp",
            mWeb: "https://static.amityonline.com/_s/Displaying_image_1_2f1cd4db8c.webp"
          }
        },
      },
    });

    // Same events/payloads — after modal open paints (no navigation, safe for tracking).
    scheduleAfterPaint(() => {
      pushToDataLayer({
        ...GA_EVENTS.get(LEAD_INITIATE),
        cta_text: "Start Your Success Story Today",
        section_name: "Real Stories",
        header_title: "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: location.href,
      });

      updateStateSession({
        parentSection: "Real Stories",
        leadStartingCta: "Start Your Success Story Today",
      });

      window?.webengage?.track("Start Your Success Story Today Clicked", {
        "Page Type": getGlobalStateValue("pageType")
      });
    });
  }

  const BORDER_CLASSES = [styles.borderYellow, styles.borderBlue, styles.borderNavy];

  return (
    <>
      <div className={cn(styles.reviewsOnlineContainer, "lining-nums")}>
        <div className={styles.header_container}>
          <h2 className={cn(styles.reviewsOnlineTitle, styles.desktopTitle)} id="review-testimonial-heading">
            Real Impact. Real Stories. Worldwide.
          </h2>
          <h2 className={cn(styles.reviewsOnlineTitle, styles.mobileTitle)} id="review-testimonial-heading-mobile">
            Real Impact. Real Stories. Worldwide.
          </h2>
          <button className={styles.sc_btn} onClick={handleBtnClicked} id="btn_start_your_success_story_today">Start Your Success Story Today</button>
        </div>
        <hr />
        <div className={cn(styles.text_caru_container, "relative")}>
          <UnivoCarousel
            ref={sliderRef}
            preset="reviews"
            settings={carouselSettings}
            className={`${styles.reviewsCarouselWrapper} relative`}
            slidesContainerClassName={styles.slidesContainer}
            slideWrapperClassName={styles.slideWrapper}
            isSmoothBehaviour={true}
            id="honest-review"
          >
            {STUDENT_STORIES_DATA_BRAND?.map((item, index) => {
               const colorClass = BORDER_CLASSES[index % BORDER_CLASSES.length];
              return (
                <div key={index} className={cn(styles.reviewsOnlineItem,colorClass)}>
                  {
                    // !!item?.text ?
                      (
                        <div className={cn(styles.photoCard,colorClass)}>
                          <div className={styles.photoWrapper}>
                            <UnivoImage
                              src={addStaticSubdomain(
                                item?.sent_by?.avatar || IMAGE_ASSETS.reviewAvatarFallback
                              )}
                              alt={item?.sent_by?.name || `reviewer-${index + 1}`}
                              fill
                              sizes="(max-width: 1023px) 90vw, 360px"
                              loading="lazy"
                              fetchPriority="low"
                              style={{ objectFit: "cover" }}
                            />
                          </div>
                          <div className={styles.reviewContent}>
                            <p className={styles.reviewQuote}>{item?.text || ""}</p>
                            <p className={styles.authorLine}>— {item?.sent_by?.name || ""}</p>
                          </div>
                        </div>
                      )
                  }
                </div>
              );
            })}
          </UnivoCarousel>
          <button
            className={clsx(styles.prevArrow)}
            onClick={handlePrev}
            disabled={isAtStart}
            aria-label="Previous"
            id="prev-btn"
          >
            <LeftArrowNoTail />
          </button>
          <button
            className={clsx(styles.nextArrow)}
            onClick={handleNext}
            aria-label="Next"
            disabled={isAtEnd}
            id="next-btn"
          >
            <RightArrowNoTail />
          </button>
        </div>
      </div>
      {
        currentVideoUrl && (
          <div
            onClick={handleCloseModal}
            className={styles.videoModalContainer}
          >
            <div className={styles.videoModalBox}>
              <div className={styles.closeButtonContainer}>
                <button
                  onClick={handleCloseModal}
                  className={styles.closeButton}
                >
                  ✕
                </button>
              </div>
              <div
                onClick={(e) => e.stopPropagation()}
                className={styles.videoModalContent}
              >
                <div className={styles.iframeContainer}>
                  {currentVideoUrl && (
                    <iframe
                      src={getYouTubeEmbedUrl(currentVideoUrl) + "?autoplay=1"}
                      title="youtube-modal-video"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

    </>
  );
};

export default Reviews2;
